
import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    // Always set up event listeners to handle responsive changes
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize);

    // Initialize carousel mode immediately - check if we're on tablet/mobile
    // and need to override columns mode
    const isTablet = window.matchMedia('(min-width: 750px) and (max-width: 1024px)').matches;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    
    if ((isTablet || isMobile) && this.dataset.displayMode === 'columns') {
      // Force carousel initialization immediately for tablet/mobile
      if (this.refs.slides?.length) {
        this.#current = 0;
        this.refs.slides.forEach((slide, index) => {
          slide.setAttribute('aria-hidden', `${index !== 0}`);
        });
      }
    }

    // Initialize carousel mode based on current viewport
    this.#updateCarouselMode();
  }

  /**
   * Determines if carousel should be enabled based on current viewport and settings
   * @returns {boolean}
   */
  #shouldEnableCarousel() {
    const displayMode = this.dataset.displayMode;
    const mobileDisplayMode = this.dataset.mobileDisplayMode;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const isTablet = window.matchMedia('(min-width: 750px) and (max-width: 1024px)').matches;

    if (isMobile) {
      return mobileDisplayMode === 'carousel';
    } else if (isTablet) {
      // Tablet always uses carousel by default
      return true;
    } else {
      return displayMode === 'carousel';
    }
  }

  /**
   * Updates carousel mode based on viewport size
   */
  #updateCarouselMode() {
    const shouldEnable = this.#shouldEnableCarousel();
    if (shouldEnable) {
      // Initialize carousel - show only first slide
      // Reset to first slide if needed
      if (this.refs.slides?.length) {
        this.#current = 0;
        this.refs.slides.forEach((slide, index) => {
          slide.setAttribute('aria-hidden', `${index !== 0}`);
        });
      }
      if (!this.#interval && this.autoplay) {
        this.play();
      }
    } else {
      this.suspend();
      // Show all slides when carousel is disabled
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
    }
  }

  /**
   * Handles window resize to update carousel mode
   */
  #handleResize = () => {
    this.#updateCarouselMode();
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    // Only update if carousel is enabled
    if (!this.#shouldEnableCarousel()) {
      return;
    }

    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
